import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useFeatureTour } from './FeatureTour.js';

// ─── First-run hint: "click a job to expand it" ───────────
// Shown once per device, the first time someone hovers a job card long enough
// to mean it. The hovered card keeps bouncing behind the notice until the user
// dismisses it, so the hint points at the thing it is describing.
const HINT_KEY = 'bvm.hint.job-expand.v1';

// Hover must be sustained this long before the hint fires — a cursor crossing
// the list on its way somewhere else should not trigger a blocking dialog.
const HOVER_DELAY_MS = 500;

// This hint stays disarmed while the feature tour is running, then waits out a
// settle period once the tour goes idle. Two tutorials landing back to back
// read as nagging, and a blocking dialog mid-tour would fight the tour's own
// cues for attention.
const SETTLE_MS = 5000;

const read = () => {
  try {
    return window.localStorage.getItem(HINT_KEY) === 'seen';
  } catch {
    return true; // storage unavailable — never nag
  }
};

const JobExpandHintContext = createContext({
  activeId: null,
  notifyHover: () => {},
  cancelHover: () => {},
});

export const useJobExpandHint = () => useContext(JobExpandHintContext);

export const JobExpandHintProvider = ({ children }) => {
  const [seen, setSeen] = useState(read);
  const [activeId, setActiveId] = useState(null);

  // Wait for the feature tour to finish, then settle, before arming. A non-null
  // stage means some part of the tour is still on screen.
  const { stage } = useFeatureTour();
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (seen) return undefined;
    if (stage !== null) {
      setArmed(false);
      return undefined;
    }
    const t = setTimeout(() => setArmed(true), SETTLE_MS);
    return () => clearTimeout(t);
  }, [stage, seen]);

  const notifyHover = useCallback((id) => {
    if (seen || !armed || !id) return;
    setActiveId((cur) => cur ?? id);
  }, [seen, armed]);

  const cancelHover = useCallback(() => {}, []);

  const dismiss = useCallback(() => {
    setActiveId(null);
    setSeen(true);
    try {
      window.localStorage.setItem(HINT_KEY, 'seen');
    } catch {
      // Nothing to persist to; the hint simply may appear again next session.
    }
  }, []);

  useEffect(() => {
    if (!activeId) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') dismiss(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeId, dismiss]);

  return (
    <JobExpandHintContext.Provider value={{ activeId, notifyHover, cancelHover }}>
      {children}
      {activeId && <JobExpandHintModal onDismiss={dismiss} />}
    </JobExpandHintContext.Provider>
  );
};

const JobExpandHintModal = ({ onDismiss }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center px-4" role="dialog" aria-modal="true">
    {/* Deliberately translucent: the bouncing job card stays visible behind it. */}
    <div className="fixed inset-0 bg-gray-900/70" />
    <div className="relative bg-gray-900 border border-gray-500/60 rounded-lg shadow-2xl max-w-sm w-full p-5">
      <button
        onClick={onDismiss}
        className="absolute top-2.5 right-3 text-gray-400 hover:text-white text-xl leading-none"
        aria-label="Dismiss hint"
      >
        &times;
      </button>
      <p className="text-sm font-semibold text-white mb-2">Click a job to expand it</p>
      <p className="text-xs text-gray-300 leading-relaxed">
        Clicking anywhere on a job card opens its full details — contact, site notes,
        chemicals and links. Click it again to collapse. The bouncing card is the one
        you just hovered.
      </p>
      <p className="text-xs text-gray-400 leading-relaxed mt-2">
        Buttons on the card (pin, edit, remove, links) still do their own thing.
      </p>
      <div className="flex justify-end mt-4">
        <button
          onClick={onDismiss}
          className="px-3.5 py-1.5 text-xs font-medium bg-green-700 text-white rounded hover:bg-green-900 transition-colors"
        >
          Got it
        </button>
      </div>
    </div>
  </div>
);

/**
 * Hover handlers for a job card. Returns props to spread, plus whether this
 * card is the one the hint is pointing at (so it can bounce).
 */
export const useHintHover = (id) => {
  const { activeId, notifyHover } = useJobExpandHint();
  const [timer, setTimer] = useState(null);

  const onMouseEnter = useCallback(() => {
    const t = setTimeout(() => notifyHover(id), HOVER_DELAY_MS);
    setTimer(t);
  }, [id, notifyHover]);

  const onMouseLeave = useCallback(() => {
    setTimer((t) => { if (t) clearTimeout(t); return null; });
  }, []);

  useEffect(() => () => { if (timer) clearTimeout(timer); }, [timer]);

  return { hintProps: { onMouseEnter, onMouseLeave }, isHinted: activeId === id };
};

export default JobExpandHintProvider;
